import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js/pure";
import { CardElement, useStripe, useElements, Elements } from "@stripe/react-stripe-js";
import { useStudio } from "../studios/StudioContext";

// "/pure" = Stripe.js is NOT injected on import. advancedFraudSignals:false = no hidden
// m-outer iframe. That iframe's internal navigations add phantom history entries in
// Safari (the embed sits inside studio sites, so it broke Back on the host page too).
loadStripe.setLoadParameters({ advancedFraudSignals: false });

// One Stripe client per publishable key, created lazily on first use.
// Key comes from studio config (useStudio) so each tenant can have its own
// Stripe account later without code changes.
const stripePromises = {};
export function getStripe(publishableKey) {
  if (!publishableKey) return null; // no Stripe key for this studio — card form stays inert instead of crashing
  if (!publishableKey) return null; // no Stripe key for this studio: card form stays inert instead of crashing
  if (!stripePromises[publishableKey]) {
    stripePromises[publishableKey] = loadStripe(publishableKey);
  }
  return stripePromises[publishableKey];
}

const CARD_STYLE = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#e25950" },
  },
  hidePostalCode: true,
};

// Inner component — must live inside Elements
function StripeCardFormInner({ amount, email, billingDetails, clientSecret, onSuccess, onError, submitLabel, disabled }) {
  const { apiBaseUrl: API } = useStudio();
  const stripe   = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements || paying) return;
    setPaying(true);
    try {
      let secret = clientSecret;
      if (!secret) {
        const intentRes  = await fetch(`${API}/payments/intent`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, amount }),
        });
        const intentData = await intentRes.json();
        if (!intentRes.ok) throw new Error(intentData.error ?? "Payment setup failed.");
        secret = intentData.clientSecret;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: billingDetails ?? { email },
        },
      });

      if (error) throw new Error(error.message);
      onSuccess(paymentIntent.id);
    } catch (err) {
      onError(err.message || "Something went wrong. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
      <div className="stripe-card-element">
        <CardElement options={CARD_STYLE} />
      </div>
      <button className="book-btn" style={{ marginTop: "1.25rem" }}
        disabled={disabled || paying} onClick={handleSubmit}>
        {paying ? "Processing…" : submitLabel}
      </button>
    </>
  );
}

// Public component — owns its own Elements context
export function StripeCardForm({ amount, email, billingDetails, clientSecret, onSuccess, onError, submitLabel = "Pay", disabled = false }) {
  const { stripePublishableKey } = useStudio();
  return (
    <Elements stripe={getStripe(stripePublishableKey)}>
      <StripeCardFormInner
        amount={amount}
        email={email}
        billingDetails={billingDetails}
        clientSecret={clientSecret}
        onSuccess={onSuccess}
        onError={onError}
        submitLabel={submitLabel}
        disabled={disabled}
      />
    </Elements>
  );
}
